import { DataGrid } from "@mui/x-data-grid";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert"; // Icon for the popup button
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Tooltip } from "@mui/material";
import FunctionsIcon from "@mui/icons-material/Functions";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";

import { getFormattedDate, getRegularDate } from "../../functions/time";
import Comment from "./popup/Comment";
import {
  setCitationPopup,
  setSummarizePopup,
  setSummaryInfoData,
  setUrl,
} from "../../redux/features/dataSlice";
import EditBookmark from "./popup/EditBookmark";
import { mapBookmarkData } from "../../constants";
import { useIsMobile } from "../../functions/isMobile";

export default function DataTable(props) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const {
    bookmarks,
    handleDeleteBookmark,
    setShowSummarizePopup,
    setSummary,
    setBookmark,
    setSummarizePopupNa,
    setCitationAvailable,
    setId,
    getBookmarks,
    setDarkBackground,
  } = props;
  const [commentPopup, setCommentPopup] = useState(false);
  const [editPopup, setEditPopup] = useState(false);
  const [collectionId, setCollectionId] = useState(null);

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
      width: undefined,
      height: undefined,
    });

    useEffect(() => {
      function handleResize() {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }

      window.addEventListener("resize", handleResize);

      handleResize();

      return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowSize;
  };

  const size = useWindowSize();

  const columns = [
    {
      field: "action",
      description: "",
      sortable: false,
      headerName: "Action",
      width: 100,
      flex: 1,
      renderCell: (params) => {
        const handleDeleteClick = () => {
          handleDeleteBookmark(selectedRow.collectionSourceID_ids);
          setAnchorEl(false);
        };

        return (
          <>
            <MoreVertIcon
              style={{ cursor: "pointer" }}
              onClick={(event) => handleMenuClick(event, params.row)}
            />
            <Menu
              id="action-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
              disableScrollLock={true}
            >
              <MenuItem onClick={handleEdit}>Edit</MenuItem>
              <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
            </Menu>
          </>
        );
      },
    },
    {
      field: "sourceName_chr",
      description: "",
      headerName: "Source",
      flex: 2,
      disableColumnMenu: true,

      width: 140,
      renderCell: (params) => (
        <div
          style={{
            width: "95%",
            padding: "0.5rem 0.5rem",
            whiteSpace: "normal",
            wordWrap: "break-word",
            overflow: "hidden",
          }}
        >
          {params.value
            ? size.width <= 750 && params.value.length > 10
              ? `${params.value.substring(0, 10)}...`
              : params.value.length > 25
              ? `${params.value.substring(0, 25)}...`
              : params.value
            : "NA"}
        </div>
      ),
    },
    {
      field: "title_chr",
      headerName: "Title",
      flex: 5,
      width: 400,
      disableColumnMenu: true,

      valueGetter: (params) => String(params.row.title_chr),
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            width: "95%",
            padding: "0 0.5rem",
          }}
        >
          <Link
            to={params.row.sourceUrl_chr}
            target="_blank"
            rel="noopener noreferrer"
          >
            {size.width <= 750 && params.value.length > 50
              ? `${params.value.substring(0, 50)}...`
              : params.value.length > 100
              ? `${params.value.substring(0, 100)}...`
              : params.value}
          </Link>
        </div>
      ),
    },
    {
      field: "isSummaryAvailable_ysn",
      description: "Summary",
      disableColumnMenu: true,
      flex: 1,
      headerName: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
          }}
        >
          <FunctionsIcon style={{ marginRight: 5 }} />
        </div>
      ),
      sortable: false,
      width: 60,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title="Edit Summary">
            <DescriptionIcon
              className="download"
              style={{ cursor: "pointer" }}
              onClick={() => {
                dispatch(
                  setSummaryInfoData({
                    publisher: params.row.sourceName_chr,
                    title: params.row.title_chr,
                    published_date: getRegularDate(
                      params.row.sourcePublishedDateTime_dtm
                    ),
                    url: params.row.sourceUrl_chr,
                  })
                );
                setShowSummarizePopup(true);
                setSummary(params.row.summary_chr);
                setBookmark(params.row);
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Add New Summary">
            <AddIcon
              className="download"
              style={{ cursor: "pointer" }}
              onClick={() => {
                dispatch(setUrl(params.row.sourceUrl_chr));
                dispatch(setSummarizePopup(true));
                dispatch(
                  setSummaryInfoData({
                    publisher: params.row.sourceName_chr,
                    title: params.row.title_chr,
                    published_date: getRegularDate(
                      params.row.sourcePublishedDateTime_dtm
                    ),
                    url: params.row.sourceUrl_chr,
                  })
                );
                setSummarizePopupNa(true);
                setBookmark(params.row);
                setId(params.row.collectionID_lng);
              }}
            />
          </Tooltip>
        ),
    },
    {
      field: "isCitationAvailable_ysn",
      headerName: "Citation",
      disableColumnMenu: true,

      description: "",
      flex: 1,
      sortable: false,
      width: 90,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title="Edit Citation">
            <ReceiptIcon
              style={{ cursor: "pointer" }}
              onClick={() => {
                setCitationAvailable(true);
                setSummaryInfoData({
                  published_date: null,
                });
                setId(params.row.collectionSourceID_ids);
                dispatch(setUrl(params.row.sourceUrl_chr));
                dispatch(setCitationPopup(true));
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Add Citation">
            <AddIcon
              style={{ cursor: "pointer" }}
              onClick={() => {
                setCitationAvailable(false);
                setId(params.row.collectionSourceID_ids);
                dispatch(setUrl(params.row.sourceUrl_chr));
                dispatch(
                  setSummaryInfoData({
                    published_date: params.row.sourcePublishedDateTime_dtm,
                    publisher: params.row.sourceName_chr,
                    title: params.row.title_chr,
                    url: params.row.sourceUrl_chr,
                  })
                );
                dispatch(setCitationPopup(true));
              }}
            />
          </Tooltip>
        ),
    },
    {
      field: "isNotesAvailable_ysn",
      disableColumnMenu: true,

      headerName: "Notes",
      description: "",
      sortable: false,
      flex: 1,
      width: 90,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title="Add Notes">
            <DescriptionIcon
              style={{ cursor: "pointer" }}
              className="download"
              onClick={() => {
                setCollectionId(params.row.collectionSourceID_ids);
                setCommentPopup(true);
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Add Notes">
            <AddIcon
              style={{ cursor: "pointer" }}
              className="download"
              onClick={() => {
                setCollectionId(params.row.collectionSourceID_ids);
                setCommentPopup(true);
              }}
            />
          </Tooltip>
        ),
    },
    {
      field: "sourcePublishedDateTime_dtm",
      disableColumnMenu: true,

      headerName: "Published",
      description: "",
      flex: 2,
      width: 120,
      type: "date",
      valueGetter: (params) => {
        return params.value ? new Date(params.value) : null;
      },
      renderCell: (params) => getFormattedDate(params.value),
    },
    {
      field: "lastUpdatedDateTime_dtm",
      disableColumnMenu: true,

      headerName: "Added",
      description: "",
      flex: 2,
      width: 120,
      type: "date",
      valueGetter: (params) => {
        return params.value ? new Date(params.value) : null;
      },
      renderCell: (params) => getFormattedDate(params.value),
    },
  ];

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(mapBookmarkData(row));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setDarkBackground(true);
    setEditPopup(true);
    handleClose();
  };

  return (
    <div className="table-save">
      {bookmarks.length === 0 ? (
        <p className="message">
          Oops! It looks like there aren't any bookmarks in this folder. Try
          adding some!
        </p>
      ) : (
        <div className="data-table">
          <DataGrid
            className="table-saved"
            sx={{
              "& .MuiDataGrid-cell:focus-within": {
                backgroundColor: "transparent",
                outline: "none",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#b4edc7",
                color: "black",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: "#b4edc7",
                color: "black",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#b4edc7",
                color: "black",
              },
            }}
            rows={bookmarks}
            getRowId={(row) => row.collectionSourceID_ids}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            pageSizeOptions={[25, 50]}
            rowHeight={70}
            hideFooterSelectedRowCount
          />
        </div>
      )}
      {commentPopup && (
        <Comment
          commentPopup={commentPopup}
          setCommentPopup={setCommentPopup}
          collectionId={collectionId}
          getBookmarks={getBookmarks}
        />
      )}
      {editPopup && (
        <EditBookmark
          collectionId={collectionId}
          getBookmarks={getBookmarks}
          customBookmarkPopup={editPopup}
          setCustomBookmarkPopup={setEditPopup}
          setDarkBackground={setDarkBackground}
          bookmark={selectedRow}
          setBookmark={setSelectedRow}
        />
      )}
    </div>
  );
}
